import { alertDialog, confirmDialog } from "@fg/ui-theme/dialogs";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { readChatPrefs, setChatPref, useChatPrefs } from "@/lib/chatPrefs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ManageTeamDialog } from "./teams/ManageTeamDialog";
import { ChannelNotifyDialog } from "./teams/ChannelNotifyDialog";
import { MeetingReminders } from "./meeting/MeetingReminders";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, BarChart3, Bookmark, Check, CheckSquare, ChevronDown, ChevronLeft, Bell, Clock, CornerDownRight, CornerUpLeft, Ellipsis, Flag, Forward, Hash, Heading, History, Languages, Loader2, Megaphone, MessageSquareText, Paperclip, Pencil, Pin, PinOff, Plus, Search, Send, Settings, Siren, Smile, Star, StarOff, Sticker, Trash2, Users, Video, X, BellOff } from "@fg/icons";
import { HubConnectionState } from "@microsoft/signalr";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    useChannelMembers,
    useChannelPosts,
    useChannelUnread,
    useChannels,
    useCreateChannel,
    useCreateTeam,
    useMessageAction,
    useMessageReactions,
    usePinToggle,
    useSendMessage,
    useSendThreadReply,
    useTeams,
    useChats
} from "@/api/hooks";
import type { ChannelMember, ChannelPost, ChatMessage, MessageImportance, Team } from "@/api/connectApi";
import { normalizeUserId } from "@/api/connectApi";
import { ConnectEvents, useConnectHub } from "@/api/useConnectHub";
import { AppFooter, useSuitePresence, type PresenceStatus } from "@fg/ui-theme";
import { cn } from "@/lib/utils";
import { flashMessage } from "@/lib/jump";
import { hasPollMarker } from "@/lib/pollMarkers";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { AppRail, type AppMode } from "./shell/AppRail";
import { WelcomePane } from "./shell/WelcomePane";
import { colourFor, listTime } from "./chat/chatUi";
import { ChatComposer, type ChatShareTarget, type MentionCandidate } from "./chat/ChatComposer";
import { PinnedBar } from "./pins/PinnedBar";
import { PollComposerDialog } from "./polls/PollComposerDialog";
import { ConversationFindBar } from "./search/ConversationFindBar";
import { ThreadPanel } from "./ThreadPanel";
import { RichInput } from "./rich/RichInput";
import type { RichEditorHandle } from "./rich/RichEditor";
import { PollsPanel } from "./PollsPanel";
import { ChatsView, type ChatTypingMap } from "./chat/ChatsView";
import { CalendarView } from "./CalendarView";
import { CallHistoryPage } from "./CallHistoryPage";
import { AttachmentPreviews } from "./AttachmentPreviews";
import { ComposerAttachments, DropOverlay } from "./files/ComposerAttachments";
import { MessageBody } from "./files/MessageBody";
import { filesFromPaste, useDriveAttachments, useFileDropZone, type DriveAttachments } from "./files/useDriveAttachments";
import { fileMarkersOf, splitFileMarkers } from "@/lib/fileMarkers";
import { QuietHoursDialog } from "./QuietHoursDialog";
import { SettingsDialog } from "./SettingsDialog";
import { UnfurlPreviews } from "./UnfurlPreviews";
import { ChannelTabs, ChannelTabFrame, WebhooksDialog, type ChannelActiveTab } from "./ChannelTabs";
import { ConversationFiles } from "./files/ConversationFiles";
import { PeerStatusProvider, type PeerNote } from "./presence/PeerStatus";
import { StatusMessageDialog } from "./presence/StatusMessageDialog";
import { useT } from "@/i18n";
import { SavedMessagesView } from "./SavedMessagesView";
import { useSaveChannelMessage } from "@/api/hooks";
import { ForwardMessageDialog } from "./ForwardMessageDialog";
import { mailTasksApi } from "@/api/mailTasksApi";
import { ReadReceiptAvatars, useReadReceiptMap } from "./ReadReceipts";
import { GifPicker } from "./GifPicker";
import { IncomingCallOverlay } from "./IncomingCallOverlay";
import { MembersPanel } from "./MembersPanel";
import { SearchOverlay } from "./SearchOverlay";
import { QuickSwitcher } from "./shell/QuickSwitcher";
import { connectParityApi, connectApi } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useAppearanceSync } from "@fg/ui-theme";
import { usePushSubscription } from "@/api/usePushSubscription";
import { TopBar } from "./shell/TopBar";
import { EmojiPickerTrigger } from "./EmojiPicker";
import { ActivityFeedView, useUnreadNotificationCount } from "./ActivityFeedView";
import { draftToHtml } from "@/lib/richHtml";
import { notifyChat, isOnConnectPage, plainSnippet } from "@/lib/chatNotify";
import type { ScheduledMessageDto } from "@/api/scheduledApi";
import { scheduledApi } from "@/api/scheduledApi";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "🎉", "🙏", "👀"];

// ── Per-channel composer drafts ──────────────────────────────────────────────
// Persisted to localStorage so a half-typed message survives a channel switch
// or a page reload. Keyed by channel id.
const DRAFT_KEY = (channelId: string) => `connect.draft.${channelId}`;

function loadDraft(channelId: string): string {
    try { return localStorage.getItem(DRAFT_KEY(channelId)) ?? ""; }
    catch { return ""; }
}
function saveDraft(channelId: string, value: string): void {
    try {
        if (value.trim().length === 0) localStorage.removeItem(DRAFT_KEY(channelId));
        else localStorage.setItem(DRAFT_KEY(channelId), value);
    } catch { /* storage full / disabled — drafts are best-effort */ }
}
function clearDraft(channelId: string): void {
    try { localStorage.removeItem(DRAFT_KEY(channelId)); } catch { /* ignore */ }
}

const APP_MODES: AppMode[] = ["teams", "dms", "groups", "calendar", "history", "saved", "activity"];

function isAppMode(value: string | null): value is AppMode {
    return value !== null && (APP_MODES as string[]).includes(value);
}

export function ConnectShell() {
    const auth = useAuth();
    const teams = useTeams();
    // Register the browser push subscription once after sign-in. No-op
    // when VAPID is disabled server-side or when the user declines the
    // browser permission prompt.
    usePushSubscription();
    // The current app lives in the URL (?view=…), so a refresh keeps you where
    // you were instead of snapping back to Chat, and Back moves between apps.
    const [searchParams, setSearchParams] = useSearchParams();
    const viewParam = searchParams.get("view");
    const mode: AppMode = isAppMode(viewParam) ? viewParam : "dms";
    const setMode = useCallback((next: AppMode) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set("view", next);
            return params;
        });
    }, [setSearchParams]);
    const navigate = useNavigate();
    const notifCount = useUnreadNotificationCount();
    const [createTeamOpen, setCreateTeamOpen] = useState(false);
    const [manageTeamId, setManageTeamId] = useState<string | null>(null);
    const [meetingNow, setMeetingNow] = useState(false);

    // Deep link: webmail's calendar and every web-push notification link to
    // "/?join=<meetingId>". Hand that straight to the /meeting/:id route so
    // the id stays visible in the address bar — the previous version stripped
    // it, which hid which meeting you were in and made a refresh drop you out.
    const deepLinkJoinId = typeof window === "undefined"
        ? null
        : new URLSearchParams(window.location.search).get("join");
    const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
    const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);

    const channels = useChannels(selectedTeamId);
    const channelUnread = useChannelUnread(mode === "teams" ? selectedTeamId : null);
    const unreadByChannel = useMemo(
        () => Object.fromEntries((channelUnread.data ?? []).map((u) => [u.channelId, u.unread])) as Record<string, number>,
        [channelUnread.data]
    );
    const members = useChannelMembers(selectedTeamId, selectedChannelId);
    const [typingUsers, setTypingUsers] = useState<Record<string, string>>({});
    const [threadParent, setThreadParent] = useState<ChatMessage | null>(null);
    // A reply to scroll to when the thread opens (search hit / pin in a thread).
    const [threadHighlight, setThreadHighlight] = useState<string | null>(null);
    const [showMembers, setShowMembers] = useState(false);
    const qc = useQueryClient();
    const chatsList = useChats();
    // The Chat badge is the list's unread total, so it clears as chats are
    // read — not just because the Chat tab was opened.
    const chatPrefs = useChatPrefs(normalizeUserId(auth.user?.profile.sub));
    // Muted chats do not count; a chat marked as unread counts as one.
    const unreadDms = (chatsList.data ?? []).reduce((n, c) => {
        const key = `${c.kind}:${c.id}`;
        if (chatPrefs.muted.includes(key)) return n;
        return n + (c.unreadCount > 0 ? c.unreadCount : chatPrefs.unread.includes(key) ? 1 : 0);
    }, 0);
    // The unread total in the browser tab, so a background tab shows it.
    useEffect(() => {
        const base = "Fowlguard Meeting";
        document.title = unreadDms > 0 ? `(${unreadDms > 99 ? "99+" : unreadDms}) ${base}` : base;
        return () => { document.title = base; };
    }, [unreadDms]);
    const [chatTyping, setChatTyping] = useState<ChatTypingMap>({});
    const myUserId = normalizeUserId(auth.user?.profile.sub);
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const [quietHoursOpen, setQuietHoursOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const accessToken = useAccessToken();
    // Per-channel notification choices, loaded up front so a channel set to "Activity only" never pops a banner.
    useQuery({
        queryKey: ["channel-notify"],
        queryFn: () => connectParityApi.getChannelNotify(accessToken),
        enabled: Boolean(accessToken),
        staleTime: 5 * 60_000
    });
    // Suite appearance ↔ account copy on the Drive BFF. Connect's token already
    // carries the Drive audience (Identity adds it to every token; see lib/drive.ts).
    useAppearanceSync(import.meta.env.VITE_DRIVE_API_URL, accessToken);
    // DM the top-bar person-suggestions asked us to open — passed down so
    // ConversationsView selects it instead of the most-recent chat.
    const [dmToOpen, setDmToOpen] = useState<string | null>(null);

    const openPersonDm = useCallback(async (userId: string) => {
        if (!accessToken) return;
        try {
            const chat = await connectParityApi.startDirectChat(userId, accessToken);
            setDmToOpen(chat.id);
            setMode("dms");
        } catch (err) {
            console.warn("Could not open chat from search suggestion", err);
        }
    }, [accessToken]);

    // Presence — tracks manual (Available / Busy / DND / BRB)
    // plus auto-away after 5 min of idle. `effective` is what we publish
    // to peers via the hub's SetStatus; it's driven off a shared hook so
    // Mail and every other SPA can reuse the same logic.
    const presence = useSuitePresence();
    const [peerPresence, setPeerPresence] = useState<Record<string, string>>({});
    // Status messages pushed by the hub since load (null = cleared); the
    // PeerStatusProvider overlays them on the /api/presence snapshot.
    const [peerNotes, setPeerNotes] = useState<Record<string, PeerNote | null>>({});

    // My own status message + "Appear offline" (server-side, per user).
    const myStatus = useQuery({
        queryKey: ["my-status", accessToken],
        queryFn: () => connectParityApi.getMyStatus(accessToken),
        enabled: Boolean(accessToken),
        staleTime: 60_000
    });
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const myNote = myStatus.data?.message
        && (!myStatus.data.expiresAt || new Date(myStatus.data.expiresAt).getTime() > Date.now())
        ? { message: myStatus.data.message, emoji: myStatus.data.emoji }
        : null;
    const appearOffline = Boolean(myStatus.data?.appearOffline);
    const setAppearOffline = useCallback(async (on: boolean) => {
        if (!accessToken) return;
        try {
            const next = await connectParityApi.setAppearOffline(on, accessToken);
            qc.setQueryData(["my-status", accessToken], next);
        } catch (err) {
            console.warn("Couldn't change Appear offline", err);
        }
    }, [accessToken, qc]);
    // Open a chat in the Chat view (from an Activity @mention).
    const openChat = useCallback((key: string | null) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set("view", "dms");
            if (key) params.set("chat", key);
            return params;
        });
    }, [setSearchParams]);

    // Close the thread panel if the selection changes (different channel or
    // the parent message was deleted live).
    useEffect(() => {
        setThreadParent(null);
    }, [selectedChannelId]);

    useEffect(() => {
        if (!selectedTeamId && teams.data && teams.data.length > 0) {
            setSelectedTeamId(teams.data[0]!.id);
        }
    }, [teams.data, selectedTeamId]);

    useEffect(() => {
        if (!channels.data) return;
        if (!selectedChannelId || !channels.data.some((c) => c.id === selectedChannelId)) {
            setSelectedChannelId(channels.data[0]?.id ?? null);
        }
    }, [channels.data, selectedChannelId]);

    // Channel posts are one infinite query per channel (with thread summaries),
    // so hub events refetch it rather than splice a flat message list.
    const refreshChannel = (channelId: string, parentId?: string | null) => {
        void qc.invalidateQueries({ queryKey: ["channel-posts", channelId] });
        if (parentId) void qc.invalidateQueries({ queryKey: ["thread", channelId, parentId] });
    };

    const hub = useConnectHub({
        on: {
            [ConnectEvents.ChannelMessageSent]: (payload) => {
                const msg = payload as ChatMessage;
                refreshChannel(msg.containerId, msg.parentMessageId);
                // Notify for other people's messages. Softer when the user is on
                // the page; louder (and an OS notification) when they're away or
                // the message is for a channel they aren't currently viewing.
                const author = normalizeUserId(msg.authorUserId);
                const bannerOff = qc.getQueryData<Record<string, { banner?: boolean }>>(["channel-notify"])?.[msg.containerId.replace(/-/g, "").toLowerCase()]?.banner === false;
                if (author && author !== myUserId && !bannerOff && !readChatPrefs(myUserId).muted.includes(`channel:${msg.containerId}`)) {
                    const onPage = isOnConnectPage();
                    const viewingThis = onPage && msg.containerId === selectedChannelId;
                    notifyChat({
                        title: msg.authorDisplayName ?? msg.authorEmail ?? "New message",
                        body: plainSnippet(msg.bodyHtml),
                        showOsNotification: !viewingThis,
                        loud: !onPage
                    });
                }
            },
            [ConnectEvents.ChannelMessageEdited]: (payload) => {
                const msg = payload as ChatMessage;
                refreshChannel(msg.containerId, msg.parentMessageId);
            },
            [ConnectEvents.ChannelMessageDeleted]: (payload) => {
                const p = payload as { channelId: string; messageId: string };
                refreshChannel(p.channelId);
                void qc.invalidateQueries({ queryKey: ["thread", p.channelId] });
                void qc.invalidateQueries({ queryKey: ["pins", "channels", p.channelId] });
            },
            [ConnectEvents.ChannelMessagePinned]: (payload) => {
                const p = payload as { channelId: string; messageId: string; pinned: boolean };
                refreshChannel(p.channelId);
                void qc.invalidateQueries({ queryKey: ["pins", "channels", p.channelId] });
            },
            [ConnectEvents.ChannelReaction]: (payload) => {
                const p = payload as { channelId: string; messageId: string };
                void qc.invalidateQueries({ queryKey: ["reactions", p.channelId, p.messageId] });
            },
            // Someone voted on / closed a poll in one of my conversations.
            [ConnectEvents.PollUpdated]: (payload) => {
                const p = payload as { pollId?: string };
                if (p.pollId) void qc.invalidateQueries({ queryKey: ["poll", p.pollId] });
            },
            [ConnectEvents.TypingStarted]: (payload) => {
                const p = payload as { channelId: string; userId: string; displayName: string };
                if (p.channelId !== selectedChannelId) return;
                setTypingUsers((prev) => ({ ...prev, [p.userId]: p.displayName || p.userId }));
            },
            [ConnectEvents.TypingStopped]: (payload) => {
                const p = payload as { channelId: string; userId: string };
                setTypingUsers((prev) => {
                    const next = { ...prev };
                    delete next[p.userId];
                    return next;
                });
            },
            [ConnectEvents.PresenceChanged]: (_payload) => {
                const p = _payload as { userId?: string; status?: string; noteChanged?: boolean; note?: PeerNote | null };
                if (p.userId && p.status) setPeerPresence((prev) => ({ ...prev, [p.userId!]: p.status! }));
                // Only a status-message change carries `noteChanged`; plain
                // presence updates leave the note alone.
                if (p.userId && p.noteChanged) {
                    const id = normalizeUserId(p.userId);
                    setPeerNotes((prev) => ({ ...prev, [id]: p.note && p.note.message ? p.note : null }));
                    // My own change from another tab.
                    if (id === myUserId) void qc.invalidateQueries({ queryKey: ["my-status"] });
                }
            },
            [ConnectEvents.DirectMessageSent]: (payload) => {
                const p = payload as {
                    chatId?: string;
                    message?: { authorUserId?: string; authorDisplayName?: string; authorEmail?: string; bodyHtml?: string };
                };
                void qc.invalidateQueries({ queryKey: ["chats"] });
                if (p.chatId) void qc.invalidateQueries({ queryKey: ["direct-chat-messages", p.chatId] });
                const author = normalizeUserId(p.message?.authorUserId);
                if (!author || author === myUserId) return;
                // A muted chat stays quiet: no chime, no banner.
                if (p.chatId && readChatPrefs(myUserId).muted.includes(`dm:${p.chatId}`)) return;
                // It may have @mentioned me — refresh Activity's badge now, not on its next poll.
                if (p.message?.bodyHtml?.includes("data-fg-mention")) void qc.invalidateQueries({ queryKey: ["notifications"] });
                // Chime + (context-aware) notification for incoming DMs.
                const onPage = isOnConnectPage();
                const viewingDms = onPage && mode === "dms";
                notifyChat({
                    title: p.message?.authorDisplayName ?? p.message?.authorEmail ?? "New direct message",
                    body: plainSnippet(p.message?.bodyHtml ?? ""),
                    showOsNotification: !viewingDms,
                    loud: !onPage
                });
            },
            // One chat changed: new group message, an edit, delete, reaction or
            // read. Group chats used to poll every 5 s with no push at all.
            [ConnectEvents.ChatUpdated]: (payload) => {
                const p = payload as {
                    kind: "dm" | "group" | "meeting";
                    id: string;
                    reason?: string;
                    authorUserId?: string;
                    authorDisplayName?: string;
                    bodyHtml?: string;
                    subject?: string;
                };
                void qc.invalidateQueries({ queryKey: ["chats"] });
                if (p.reason === "read") return;
                // People were added to a group chat (FPT-8): its member list changed.
                if (p.reason === "members") {
                    void qc.invalidateQueries({ queryKey: ["conversation-members", p.id] });
                    return;
                }
                if (p.reason === "pins") {
                    void qc.invalidateQueries({ queryKey: ["pins", p.kind === "dm" ? "dms" : "conversations", p.id] });
                    return;
                }
                const messagesKey = p.kind === "dm" ? "direct-chat-messages" : p.kind === "meeting" ? "meeting-chat" : "conversation-messages";
                void qc.invalidateQueries({ queryKey: [messagesKey, p.id] });
                if (p.kind === "meeting") void qc.invalidateQueries({ queryKey: ["meeting-recap", p.id] });
                if (p.reason === "reaction") {
                    const scope = p.kind === "dm" ? "dms" : p.kind === "meeting" ? "meetings" : "conversations";
                    void qc.invalidateQueries({ queryKey: ["chat-reactions", scope, p.id] });
                }
                if (p.reason === "created") void qc.invalidateQueries({ queryKey: ["conversations"] });
                const author = normalizeUserId(p.authorUserId);
                if (p.reason === "message" && author && author !== myUserId && !readChatPrefs(myUserId).muted.includes(`${p.kind}:${p.id}`)) {
                    if (p.bodyHtml?.includes("data-fg-mention")) void qc.invalidateQueries({ queryKey: ["notifications"] });
                    const onPage = isOnConnectPage();
                    notifyChat({
                        title: p.kind === "meeting" && p.subject
                            ? `${p.authorDisplayName ?? "Someone"} · ${p.subject}`
                            : p.authorDisplayName ?? "New message",
                        body: plainSnippet(p.bodyHtml ?? ""),
                        showOsNotification: !(onPage && (mode === "dms" || mode === "groups")),
                        loud: !onPage
                    });
                }
            },
            // A meeting recording landed in Drive — refresh that meeting's
            // Recordings list wherever it's open (calendar details).
            [ConnectEvents.MeetingRecordingAdded]: (payload) => {
                const p = payload as { meetingId?: string };
                if (p.meetingId) void qc.invalidateQueries({ queryKey: ["meeting-recordings", p.meetingId] });
            },
            [ConnectEvents.ChatTyping]: (payload) => {
                const p = payload as { kind: string; chatId: string; userId: string; displayName?: string; started: boolean };
                const key = `${p.kind}:${p.chatId}`;
                setChatTyping((prev) => {
                    const current = { ...(prev[key] ?? {}) };
                    if (p.started) current[p.userId] = { name: p.displayName || "Someone", at: Date.now() };
                    else delete current[p.userId];
                    return { ...prev, [key]: current };
                });
            }
        }
    });

    useEffect(() => {
        if (!selectedChannelId || hub.state !== HubConnectionState.Connected) return;
        void hub.joinChannel(selectedChannelId);
        setTypingUsers({});
        return () => {
            void hub.leaveChannel(selectedChannelId);
        };
    }, [selectedChannelId, hub]);

    // Publish presence to the hub whenever the effective status changes
    // (manual picker OR auto-away idle flip). The server broadcasts
    // PresenceChanged to everyone else, which our own PresenceChanged
    // handler above already writes into state for the local roster.
    useEffect(() => {
        if (hub.state !== HubConnectionState.Connected) return;
        void hub.setStatus(presence.effective);
    }, [hub, presence.effective]);

    const selectedChannel = useMemo(
        () => channels.data?.find((c) => c.id === selectedChannelId) ?? null,
        [channels.data, selectedChannelId]
    );

    const displayName =
        auth.user?.profile.name ?? auth.user?.profile.email ?? "Signed in";

    // "Meet now" from the welcome screen: an instant video meeting to share.
    const meetNowFromHome = async () => {
        if (meetingNow || !accessToken) return;
        setMeetingNow(true);
        try {
            const m = await connectParityApi.createMeeting({
                subject: `${displayName}'s meeting`,
                scheduledStart: new Date().toISOString(),
                kind: "video"
            }, accessToken);
            await connectParityApi.startMeeting(m.id, accessToken);
            navigate(`/meeting/${m.id}`);
        } catch (err) {
            console.warn("Meet now failed", err);
        } finally {
            setMeetingNow(false);
        }
    };

    const [showShortcuts, setShowShortcuts] = useState(false);
    // Quick switcher / command box: Ctrl+K, Ctrl+G or Ctrl+/ (Ctrl+E focuses search).
    const [switcher, setSwitcher] = useState<{ initial: string } | null>(null);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
            const k = e.key.toLowerCase();
            if (k === "k" || k === "g") { e.preventDefault(); setSwitcher({ initial: "" }); }
            else if (k === "/") { e.preventDefault(); setSwitcher({ initial: "/" }); }
            else if (k === "e") {
                e.preventDefault();
                const input = document.getElementById("connect-search-input") as HTMLInputElement | null;
                input?.focus();
                input?.select();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Global keyboard shortcuts. Pause while focus is in an editable
    // field so typing a `/` in the search box doesn't re-trigger
    // focus and `?` while composing doesn't open the cheat sheet.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            const tag = target?.tagName ?? "";
            const editing = tag === "INPUT" || tag === "TEXTAREA" ||
                target?.isContentEditable === true;
            if (showShortcuts) return;
            // Ctrl/Cmd/Alt + a key belongs to the browser (Ctrl+1 switches its tabs) or to a dialog, not to these single-key shortcuts.
            if (e.ctrlKey || e.metaKey || e.altKey) return;

            if (e.key === "?") {
                if (editing) return;
                e.preventDefault();
                setShowShortcuts(true);
                return;
            }
            if (editing) return;
            if (e.key === "/") {
                e.preventDefault();
                const input = document.getElementById("connect-search-input") as HTMLInputElement | null;
                input?.focus();
                input?.select();
                return;
            }
            // 1-6 jump to a mode. Modifier-free single-digit so chat
            // power users can flip sections at the speed of typing.
            // Browser default for plain digits is no-op, so e.preventDefault
            // is mainly defensive against page-level handlers.
            const numericMode: Record<string, AppMode> = {
                "1": "teams",
                "2": "dms",
                "3": "groups",
                "4": "calendar",
                "5": "history",
                "6": "saved",
                "7": "activity"
            };
            const nextMode = numericMode[e.key];
            if (nextMode) {
                e.preventDefault();
                setMode(nextMode);
                return;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showShortcuts]);

    if (deepLinkJoinId) {
        return <Navigate to={`/meeting/${deepLinkJoinId}`} replace />;
    }

    return (
        <PeerStatusProvider livePresence={peerPresence} liveNotes={peerNotes}>
        <div className="flex h-[100dvh] flex-col bg-background text-foreground">
            <TopBar
                onSearch={(q) => setSearchQuery(q.length >= 2 ? q : null)}
                onOpenPerson={(userId) => void openPersonDm(userId)}
                // Open the calendar with the new-meeting form already showing —
                // it used to only switch to the calendar.
                onNewMeeting={() => setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set("view", "calendar");
                    params.set("new", "1");
                    return params;
                })}
                onOpenQuietHours={() => setQuietHoursOpen(true)}
                onOpenHelp={() => setShowShortcuts(true)}
                onOpenSettings={() => setSettingsOpen(true)}
                presenceStatus={presence.manual}
                onChangeStatus={(s) => {
                    presence.setManual(s as PresenceStatus);
                    // Picking a status means "be seen again".
                    if (appearOffline) void setAppearOffline(false);
                }}
                statusNote={myNote}
                onOpenStatusMessage={() => setStatusDialogOpen(true)}
                appearOffline={appearOffline}
                onSetAppearOffline={(on) => void setAppearOffline(on)}
            />
            {/* Suite-shell look (approved mock-up): the 48px rail, then the
                current app on the soft canvas as white cards. Chat is two
                cards — the list and the conversation — like Mail and Drive;
                every other view is one card. */}
            <div className="flex min-h-0 flex-1 bg-[var(--connect-canvas)]">
            <AppRail
                mode={mode}
                onChange={setMode}
                unreadChats={unreadDms}
                activityCount={notifCount}
                hubState={hub.state}
            />
            <main className={cn(
                "flex min-h-0 min-w-0 flex-1 overflow-hidden",
                mode === "dms" || mode === "groups"
                    ? "p-1.5 sm:p-2"
                    : "m-1.5 rounded-lg bg-card shadow-[var(--connect-shadow-4)] sm:m-2"
            )}>

            {mode === "teams" ? (
                <>
                    <aside className={cn(
                        "w-full shrink-0 flex-col border-r md:flex md:w-64",
                        // Phone layout is master/detail: the channel
                        // list IS the screen until you pick a channel, then the
                        // conversation takes over. Both were rendered side by
                        // side before, leaving ~70px for messages on a 390px
                        // phone once the 64px rail and 256px list were drawn.
                        selectedChannelId ? "hidden" : "flex"
                    )}>
                        <TeamsPanel
                            teams={teams.data ?? []}
                            selectedTeamId={selectedTeamId}
                            onSelectTeam={setSelectedTeamId}
                            createOpen={createTeamOpen}
                            onCreateOpenChange={setCreateTeamOpen}
                            onManage={setManageTeamId}
                        />
                        <ChannelsPanel
                            teamId={selectedTeamId}
                            channels={channels.data ?? []}
                            selectedChannelId={selectedChannelId}
                            onSelectChannel={setSelectedChannelId}
                            unread={unreadByChannel}
                            me={myUserId}
                        />
                    </aside>

                    <section className={cn(
                        "min-h-0 flex-1 flex-col md:flex",
                        selectedChannelId ? "flex" : "hidden"
                    )}>
                        {selectedChannel ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setSelectedChannelId(null)}
                                    className="flex items-center gap-1 border-b px-3 py-2 text-sm text-muted-foreground md:hidden"
                                >
                                    <ChevronLeft className="h-4 w-4" aria-hidden />
                                    Channels
                                </button>
                                <ChannelView
                                    channelId={selectedChannel.id}
                                    channelName={selectedChannel.name}
                                    typingUsers={typingUsers}
                                    onTyping={(started) => void hub.typing(selectedChannel.id, started)}
                                    presence={peerPresence}
                                    members={members.data ?? []}
                                    onOpenThread={(msg, highlight) => {
                                        setThreadParent(msg);
                                        setThreadHighlight(highlight ?? null);
                                    }}
                                    membersOpen={showMembers}
                                    onToggleMembers={() => setShowMembers((v) => !v)}
                                    channelKind={selectedChannel.kind}
                                />
                            </>
                        ) : (
                            <WelcomePane
                                name={displayName}
                                hasTeams={(teams.data ?? []).length > 0}
                                busy={meetingNow}
                                onCreateTeam={() => setCreateTeamOpen(true)}
                                onStartChat={() => setMode("dms")}
                                onMeetNow={() => void meetNowFromHome()}
                            />
                        )}
                    </section>

                    {selectedChannel && threadParent ? (
                        <ThreadPanel
                            key={threadParent.id}
                            channelId={selectedChannel.id}
                            channelName={selectedChannel.name}
                            parent={threadParent}
                            members={members.data ?? []}
                            highlightId={threadHighlight}
                            onClose={() => {
                                setThreadParent(null);
                                setThreadHighlight(null);
                            }}
                        />
                    ) : null}

                    {selectedChannel && showMembers ? (
                        <MembersPanel
                            members={members.data ?? []}
                            onClose={() => setShowMembers(false)}
                        />
                    ) : null}
                </>
            ) : mode === "dms" || mode === "groups" ? (
                // One Chat list for 1:1 and group chats.
                <ChatsView
                    initialChatId={dmToOpen}
                    typing={chatTyping}
                    onTyping={(kind, id, started) => void hub.typingInChat(kind, id, started)}
                />
            ) : mode === "history" ? (
                <CallHistoryPage />
            ) : mode === "saved" ? (
                <SavedMessagesView
                    onJump={(s) => {
                        if (s.containerKind === "channel") {
                            setMode("teams");
                            setSelectedChannelId(s.containerId);
                        } else if (s.containerKind === "dm") {
                            setMode("dms");
                        } else {
                            setMode("dms");
                        }
                    }}
                />
            ) : mode === "activity" ? (
                <ActivityFeedView
                    onOpenChannel={(channelId) => {
                        setMode("teams");
                        setSelectedChannelId(channelId);
                    }}
                    onOpenDm={(id) => openChat(id ? `dm:${id}` : null)}
                    onOpenConversation={(id) => openChat(id ? `group:${id}` : null)}
                    onOpenMeetingChat={(id) => openChat(id ? `meeting:${id}` : null)}
                    onOpenTeam={(id) => {
                        setMode("teams");
                        if (id) setSelectedTeamId(id);
                    }}
                />
            ) : (
                <CalendarView />
            )}
            </main>
            </div>
            <IncomingCallOverlay />
            <MeetingReminders />
            {quietHoursOpen ? (
                <QuietHoursDialog onClose={() => setQuietHoursOpen(false)} />
            ) : null}
            {manageTeamId && teams.data?.find((t) => t.id === manageTeamId) ? (
                <ManageTeamDialog
                    team={teams.data.find((t) => t.id === manageTeamId)!}
                    me={myUserId}
                    onClose={() => setManageTeamId(null)}
                    onDeleted={() => {
                        setManageTeamId(null);
                        setSelectedTeamId(null);
                        setSelectedChannelId(null);
                        void qc.invalidateQueries({ queryKey: ["teams"] });
                    }}
                />
            ) : null}
            {settingsOpen ? (
                <SettingsDialog onClose={() => setSettingsOpen(false)} onOpenQuietHours={() => setQuietHoursOpen(true)} />
            ) : null}
            {statusDialogOpen ? (
                <StatusMessageDialog current={myStatus.data} onClose={() => setStatusDialogOpen(false)} />
            ) : null}
            {switcher ? (
                <QuickSwitcher
                    chats={chatsList.data ?? []}
                    initial={switcher.initial}
                    onClose={() => setSwitcher(null)}
                    actions={{
                        openChat,
                        openPerson: (id) => void openPersonDm(id),
                        setMode,
                        showUnread: () => {
                            setMode("dms");
                            window.setTimeout(() => window.dispatchEvent(new CustomEvent("fg:chat-filter", { detail: "unread" })), 50);
                        },
                        setStatus: (s) => {
                            presence.setManual(s === "offline" ? "online" : s);
                            void setAppearOffline(s === "offline");
                        },
                        search: (q) => setSearchQuery(q),
                        showHelp: () => setShowShortcuts(true)
                    }}
                />
            ) : null}
            {searchQuery ? (
                <SearchOverlay
                    query={searchQuery}
                    onClose={() => setSearchQuery(null)}
                    onOpenChannel={(channelId) => {
                        setMode("teams");
                        setSelectedChannelId(channelId);
                    }}
                    onOpenDirectChat={() => setMode("dms")}
                    onOpenConversation={() => setMode("dms")}
                />
            ) : null}
            {showShortcuts ? (
                <ShortcutsDialog onClose={() => setShowShortcuts(false)} />
            ) : null}
            <AppFooter />
        </div>
        </PeerStatusProvider>
    );
}

/**
 * Modal cheat-sheet dialog opened by the `?` shortcut. Same shape as
 * the Mail + Drive cheat sheets — fixed-position overlay with
 * backdrop click-to-close + Esc — so the muscle memory transfers.
 */
function ShortcutsDialog({ onClose }: { onClose: () => void }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const rows: Array<[string, string]> = [
        ["?",   "Show this help"],
        ["/",   "Focus search"],
        ["Ctrl+E", "Focus search"],
        ["Ctrl+K", "Quick switcher: go to a chat, a person or a section"],
        ["Ctrl+/", "Commands: /dnd /busy /away /available /offline /unread /mentions /saved /calendar /goto"],
        ["Up arrow", "In an empty message box: edit your last message"],
        ["1",   "Jump to Channels"],
        ["2",   "Jump to Chats"],
        ["3",   "Jump to Group chats"],
        ["4",   "Jump to Calendar"],
        ["5",   "Jump to Call history"],
        ["6",   "Jump to Saved messages"],
        ["7",   "Jump to Activity feed"],
        ["Esc", "Close dialog / clear search"],
    ];
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Keyboard shortcuts"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-lg border bg-background p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-base font-semibold">Keyboard shortcuts</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
                        aria-label="Close shortcuts dialog"
                    >
                        Esc
                    </button>
                </div>
                <table className="w-full text-sm">
                    <tbody>
                        {rows.map(([keys, desc]) => (
                            <tr key={desc} className="border-t first:border-0">
                                <td className="w-32 py-1.5 font-mono text-xs text-muted-foreground">{keys}</td>
                                <td className="py-1.5">{desc}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="mt-3 text-[11px] text-muted-foreground">
                    Shortcuts pause while you're typing in a text field.
                </p>
            </div>
        </div>
    );
}

function initials(name: string): string {
    const words = name.split(/\s+/).filter(Boolean);
    const letters = words.length <= 1
        ? name.slice(0, 2)
        : words[0]!.charAt(0) + words[words.length - 1]!.charAt(0);
    return letters.toUpperCase();
}

function TeamsPanel({ teams, selectedTeamId, onSelectTeam, createOpen, onCreateOpenChange, onManage }: {
    teams: Team[];
    /** Opens the Manage team dialog. */
    onManage: (teamId: string) => void;
    selectedTeamId: string | null;
    onSelectTeam: (id: string) => void;
    /** Lifted so the welcome screen's "Create a team" can open the form. */
    createOpen: boolean;
    onCreateOpenChange: (open: boolean) => void;
}) {
    const create = useCreateTeam();
    const open = createOpen;
    const setOpen = (next: boolean | ((current: boolean) => boolean)) =>
        onCreateOpenChange(typeof next === "function" ? next(open) : next);
    const [draft, setDraft] = useState({ slug: "", name: "" });

    return (
        <div className="border-b p-3">
            <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Channels</span>
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-accent"
                    aria-label="New team"
                >
                    <Plus className="h-4 w-4" />
                </button>
            </div>
            {open ? (
                <div className="mb-2 space-y-1">
                    <input
                        value={draft.slug}
                        onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value.toLowerCase() }))}
                        placeholder="slug (a-z 0-9 -)"
                        className="h-8 w-full rounded-md border px-2 text-sm"
                    />
                    <input
                        value={draft.name}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        placeholder="Display name"
                        className="h-8 w-full rounded-md border px-2 text-sm"
                    />
                    <div className="flex gap-1">
                        <button
                            type="button"
                            onClick={async () => {
                                if (!draft.slug || !draft.name) return;
                                const t = await create.mutateAsync(draft);
                                onSelectTeam(t.id);
                                setOpen(false);
                                setDraft({ slug: "", name: "" });
                            }}
                            className="h-7 flex-1 rounded-md bg-primary text-xs text-primary-foreground hover:bg-primary/90"
                        >
                            Create
                        </button>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="h-7 rounded-md border px-2 text-xs"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : null}
            <ul className="space-y-0.5">
                {teams.map((t) => (
                    <li key={t.id} className="group relative">
                        <button
                            type="button"
                            onClick={() => onSelectTeam(t.id)}
                            aria-current={selectedTeamId === t.id ? "true" : undefined}
                            className={cn(
                                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                selectedTeamId === t.id && "bg-accent font-medium"
                            )}
                        >
                            <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[11px] font-bold text-white shadow-sm", colourFor(t.name))} aria-hidden>
                                {initials(t.name)}
                            </span>
                            <span className="truncate">{t.name}</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onManage(t.id)}
                            aria-label={`Manage team ${t.name}`}
                            title="Manage team"
                            className="absolute right-1 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground focus-visible:flex group-focus-within:flex group-hover:flex"
                        >
                            <Settings className="h-3.5 w-3.5" />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function ChannelsPanel({
    teamId,
    channels,
    selectedChannelId,
    onSelectChannel,
    unread,
    me
}: {
    me: string;
    teamId: string | null;
    channels: { id: string; name: string; description: string | null; isDefault: boolean }[];
    selectedChannelId: string | null;
    onSelectChannel: (id: string) => void;
    /** Unread messages per channel id (other people's, since I last read it). */
    unread: Record<string, number>;
}) {
    const create = useCreateChannel(teamId ?? "");
    const [name, setName] = useState("");
    const [kind, setKind] = useState<"Standard" | "Private" | "Announcement">("Standard");
    const prefs = useChatPrefs(me);
    const [notifyFor, setNotifyFor] = useState<{ id: string; name: string } | null>(null);
    // Favourites first (in the order starred), then the rest.
    const favourites = prefs.pinned.flatMap((k) => channels.filter((c) => `channel:${c.id}` === k));
    const others = channels.filter((c) => !prefs.pinned.includes(`channel:${c.id}`));

    if (!teamId) {
        return (
            <div className="p-3 text-xs text-muted-foreground">
                Create a team with <span className="font-medium text-foreground">+</span> above — its channels show up here.
            </div>
        );
    }
    return (
        <div className="flex min-h-0 flex-1 flex-col p-3">
            <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Channels</span>
            </div>
            <ul className="flex-1 space-y-0.5 overflow-auto">
                {favourites.length > 0 ? (
                    <li className="flex items-center gap-1 px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" aria-hidden><Star className="h-3 w-3" /> Favourites</li>
                ) : null}
                {[...favourites, ...others].map((c, idx) => {
                    // The open channel is being read — no badge for it.
                    const n = selectedChannelId === c.id ? 0 : unread[c.id] ?? 0;
                    const key = `channel:${c.id}`;
                    const starred = prefs.pinned.includes(key);
                    const muted = prefs.muted.includes(key);
                    return (
                        <li key={c.id} className="group relative">
                            {idx === favourites.length && favourites.length > 0 ? <div className="mb-0.5 mt-1 border-t pt-1" /> : null}
                            <button
                                type="button"
                                onClick={() => onSelectChannel(c.id)}
                                aria-current={selectedChannelId === c.id ? "true" : undefined}
                                aria-label={n > 0 ? `${c.name}, ${n} unread` : undefined}
                                className={cn(
                                    "flex w-full items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                    selectedChannelId === c.id && "bg-accent",
                                    n > 0 && "font-semibold"
                                )}
                            >
                                <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="truncate">{c.name}</span>
                                {muted ? <BellOff className="h-3 w-3 shrink-0 text-muted-foreground" aria-label="Muted" /> : null}
                                {n > 0 && !muted ? (
                                    <span className="ml-auto shrink-0 rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-4 text-primary-foreground group-hover:hidden" aria-hidden>
                                        {n > 99 ? "99+" : n}
                                    </span>
                                ) : null}
                            </button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        aria-label={`More options for ${c.name}`}
                                        title="More options"
                                        className="absolute right-1 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-background hover:text-foreground focus-visible:flex group-focus-within:flex group-hover:flex data-[state=open]:flex"
                                    >
                                        <Ellipsis className="h-3.5 w-3.5" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onSelect={() => setChatPref(me, "pinned", key)}>{starred ? <StarOff className="mr-2 h-4 w-4" /> : <Star className="mr-2 h-4 w-4" />}{starred ? "Remove from favourites" : "Add to favourites"}</DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => setChatPref(me, "muted", key)}><BellOff className="mr-2 h-4 w-4" />{muted ? "Unmute" : "Mute"}</DropdownMenuItem>
                                    <DropdownMenuItem onSelect={() => setNotifyFor({ id: c.id, name: c.name })}><Bell className="mr-2 h-4 w-4" />Channel notifications…</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </li>
                    );
                })}
            </ul>
            {notifyFor ? <ChannelNotifyDialog channelId={notifyFor.id} channelName={notifyFor.name} onClose={() => setNotifyFor(null)} /> : null}
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    const n = name.trim().toLowerCase().replace(/\s+/g, "-");
                    if (!n) return;
                    create.mutate({ name: n, kind }, { onSuccess: () => { setName(""); setKind("Standard"); } });
                }}
                className="mt-2 flex gap-1"
            >
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="new-channel"
                    className="h-7 min-w-0 flex-1 rounded-md border px-2 text-xs"
                />
                <select
                    value={kind}
                    onChange={(e) => setKind(e.target.value as typeof kind)}
                    aria-label="Channel type"
                    title="Standard: everyone in the team. Private: only people you add. Announcement: only owners post."
                    className="h-7 w-[5.5rem] shrink-0 rounded-md border bg-background px-1 text-xs"
                >
                    <option value="Standard">Standard</option>
                    <option value="Private">Private</option>
                    <option value="Announcement">Announce</option>
                </select>
                <button type="submit" className="h-7 rounded-md bg-primary px-2 text-xs text-primary-foreground">
                    Add
                </button>
            </form>
        </div>
    );
}

function ChannelView({
    channelId,
    channelName,
    typingUsers,
    onTyping,
    presence,
    members,
    onOpenThread,
    membersOpen,
    onToggleMembers,
    channelKind
}: {
    channelId: string;
    channelName: string;
    /** "Standard" | "Private" | "Announcement". */
    channelKind?: string;
    typingUsers: Record<string, string>;
    onTyping: (started: boolean) => void;
    presence: Record<string, string>;
    members: ChannelMember[];
    /** Open a post's thread; `highlightReplyId` scrolls to one reply in it. */
    onOpenThread: (msg: ChatMessage, highlightReplyId?: string | null) => void;
    membersOpen: boolean;
    onToggleMembers: () => void;
}) {
    // Jumping to a post (search hit / pin) that isn't loaded shows the posts
    // around it (`anchor`) until "Back to latest".
    const [anchor, setAnchor] = useState<string | null>(null);
    const [pendingJump, setPendingJump] = useState<{ postId: string; replyId: string | null } | null>(null);
    const [findOpen, setFindOpen] = useState(false);
    const posts = useChannelPosts(channelId, anchor);
    const pages = posts.data?.pages;
    // Pages come newest first; each page is oldest → newest.
    const postList = useMemo(() => (pages ? [...pages].reverse().flatMap((p) => p.posts) : []), [pages]);
    const firstPage = pages?.[0];
    const canModerate = firstPage?.canModerate ?? false;
    const postMessages = useMemo(() => postList.map((p) => p.message), [postList]);
    // My read time from before this visit — "New" markers stay put while I read.
    const [readAtOpen, setReadAtOpen] = useState<{ channelId: string; at: string | null } | null>(null);
    useEffect(() => {
        if (firstPage && readAtOpen?.channelId !== channelId) {
            setReadAtOpen({ channelId, at: firstPage.myLastReadAt ?? null });
        }
    }, [firstPage, channelId, readAtOpen]);
    const readAt = readAtOpen?.channelId === channelId ? readAtOpen.at : null;
    const send = useSendMessage(channelId);
    const [draft, setDraft] = useState("");
    const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isTypingRef = useRef(false);
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [activeTab, setActiveTab] = useState<ChannelActiveTab>({ kind: "posts" });
    const [webhooksOpen, setWebhooksOpen] = useState(false);
    const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
    const navigate = useNavigate();
    const meetToken = useAccessToken();
    const [startingMeeting, setStartingMeeting] = useState(false);

    // Files for this channel: dropped on the message list or the composer,
    // uploaded to the sender's Drive and shared with the channel's members
    // (view by default) on send. Switching channel drops unsent ones.
    const attachments = useDriveAttachments("view");
    const clearAttachments = attachments.clear;
    useEffect(() => { clearAttachments(); }, [channelId, clearAttachments]);
    const listDrop = useFileDropZone(attachments.addFiles);

    // "Meet now": a native meeting linked to this channel, with a join card
    // posted to the conversation. The only option here used to be "Insert
    // meeting link", which created a meeting with an external provider.
    const meetNow = async () => {
        if (startingMeeting) return;
        setStartingMeeting(true);
        try {
            const m = await connectParityApi.createMeeting({
                subject: `Meeting in #${channelName}`,
                scheduledStart: new Date().toISOString(),
                channelId,
                kind: "video"
            }, meetToken);
            await connectParityApi.startMeeting(m.id, meetToken);
            send.mutate(`<p>📹 Started a meeting in #${escapeHtml(channelName)} — <a href="/meeting/${m.id}">Join</a></p>`);
            navigate(`/meeting/${m.id}`);
        } catch (err) {
            console.warn("Meet now failed", err);
        } finally {
            setStartingMeeting(false);
        }
    };

    // When the channel changes, snap back to Posts so a stale custom-
    // tab selection doesn't carry over to a different channel, and restore
    // this channel's saved draft (per-channel drafts —
    // switching away no longer loses what you were typing).
    useEffect(() => {
        setActiveTab({ kind: "posts" });
        setDraft(loadDraft(channelId));
        setAnchor(null);
        setPendingJump(null);
        setFindOpen(false);
    }, [channelId]);

    // Scrolling: open at the newest post; follow new posts only when you're
    // already at the bottom; keep your place when older posts load above.
    const atBottomRef = useRef(true);
    const scrollState = useRef<{ el: HTMLDivElement; key: string; newest?: string; oldest?: string; height: number } | null>(null);
    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const key = `${channelId}:${anchor ?? ""}`;
        const newest = postList[postList.length - 1]?.message.id;
        const oldest = postList[0]?.message.id;
        const prev = scrollState.current;
        if (!prev || prev.el !== el || prev.key !== key || (!prev.newest && newest)) {
            el.scrollTop = el.scrollHeight;
        } else if (prev.oldest !== oldest && prev.newest === newest) {
            el.scrollTop += el.scrollHeight - prev.height;
        } else if (prev.newest !== newest && atBottomRef.current) {
            el.scrollTop = el.scrollHeight;
        }
        scrollState.current = { el, key, newest, oldest, height: el.scrollHeight };
    });

    // Mark read once the posts (and my previous read time) have loaded, and as
    // new posts or replies land. Drives LastReadAt — everyone else's "Seen by"
    // strip and my unread badges.
    const channelReadToken = useAccessToken();
    const qcRead = useQueryClient();
    const postsLoaded = Boolean(firstPage);
    const latestPostAt = postList[postList.length - 1]?.message.sentAt;
    const replyTotal = postList.reduce((n, p) => n + p.replyCount, 0);
    useEffect(() => {
        if (!channelId || !channelReadToken || !postsLoaded) return;
        void connectParityApi.markChannelRead(channelId, channelReadToken)
            .then(() => qcRead.invalidateQueries({ queryKey: ["channel-unread"] }))
            .catch(() => { /* best-effort */ });
    }, [channelId, channelReadToken, postsLoaded, latestPostAt, replyTotal, qcRead]);

    // Pre-compute the (latest-message-each-member-has-seen) map once
    // per render so MessageItem can ask it in O(1).
    const receiptsByMessage = useReadReceiptMap(members, postMessages);

    const onInput = useCallback(
        (value: string) => {
            setDraft(value);
            saveDraft(channelId, value);
            if (!isTypingRef.current) {
                isTypingRef.current = true;
                onTyping(true);
            }
            if (typingTimer.current) clearTimeout(typingTimer.current);
            typingTimer.current = setTimeout(() => {
                isTypingRef.current = false;
                onTyping(false);
            }, 2500);
        },
        [onTyping, channelId]
    );

    const typingLabel = useMemo(() => {
        const names = Object.values(typingUsers).filter(Boolean);
        if (names.length === 0) return null;
        if (names.length === 1) return `${names[0]} is typing…`;
        if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
        return "Several people are typing…";
    }, [typingUsers]);

    // Jump to a post, or to a reply (opens its post's thread), from search or a pin.
    const jumpTo = (messageId: string, parentMessageId?: string | null) => {
        setActiveTab({ kind: "posts" });
        const postId = parentMessageId ?? messageId;
        setPendingJump({ postId, replyId: parentMessageId ? messageId : null });
        if (!postList.some((p) => p.message.id === postId)) setAnchor(postId);
    };
    useEffect(() => {
        if (!pendingJump) return;
        const post = postList.find((p) => p.message.id === pendingJump.postId);
        if (post) {
            const jump = pendingJump;
            setPendingJump(null);
            if (jump.replyId) onOpenThread(post.message, jump.replyId);
            window.requestAnimationFrame(() => flashMessage(`msg-${post.message.id}`));
        } else if (posts.isSuccess && !posts.isFetching && anchor === pendingJump.postId) {
            // Not there even around itself — deleted since. Stop waiting.
            setPendingJump(null);
        }
    }, [pendingJump, postList, posts.isSuccess, posts.isFetching, anchor, onOpenThread]);

    return (
        <>
            <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
                <Hash className="h-4 w-4 text-muted-foreground" />
                <h1 className="text-sm font-semibold">{channelName}</h1>
                <span className="ml-2 text-xs text-muted-foreground">
                    {Object.values(presence).filter((s) => s === "online").length} online
                </span>
                <button
                    type="button"
                    onClick={() => setFindOpen((v) => !v)}
                    aria-pressed={findOpen}
                    aria-label={`Search #${channelName}`}
                    title="Search this channel"
                    className={cn(
                        "ml-auto flex h-7 w-7 items-center justify-center rounded-md border hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        findOpen && "bg-accent"
                    )}
                >
                    <Search className="h-3.5 w-3.5" />
                </button>
                <button
                    type="button"
                    onClick={onToggleMembers}
                    aria-pressed={membersOpen}
                    aria-label="Show members"
                    title="Members"
                    className={cn(
                        "flex h-7 items-center gap-1 rounded-md border px-2 text-xs hover:bg-accent",
                        membersOpen && "bg-accent"
                    )}
                >
                    <Users className="h-3.5 w-3.5" />
                    {members.length}
                </button>
                <button
                    type="button"
                    onClick={() => void meetNow()}
                    disabled={startingMeeting}
                    title="Start a meeting in this channel"
                    className="flex h-7 items-center gap-1 rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50"
                >
                    <Video className="h-3.5 w-3.5" /> Connect now
                </button>
            </header>
            {findOpen ? (
                <ConversationFindBar
                    scope="channels"
                    id={channelId}
                    label={`#${channelName}`}
                    onJump={(hit) => jumpTo(hit.messageId, hit.parentMessageId)}
                    onClose={() => setFindOpen(false)}
                />
            ) : null}
            <ChannelTabs
                channelId={channelId}
                active={activeTab}
                onChange={setActiveTab}
                onOpenWebhooks={() => setWebhooksOpen(true)}
            />
            {webhooksOpen ? (
                <WebhooksDialog channelId={channelId} onClose={() => setWebhooksOpen(false)} />
            ) : null}
            {activeTab.kind === "tab" ? (
                <ChannelTabFrame tab={activeTab.tab} />
            ) : activeTab.kind === "files" ? (
                <ConversationFiles scope="channels" id={channelId} label={`#${channelName}`} />
            ) : (
                <>
            <PinnedBar scope="channels" id={channelId} onJump={(p) => jumpTo(p.messageId, p.parentMessageId)} />
            <PollsPanel channelId={channelId} />
            {anchor ? (
                <div className="flex shrink-0 items-center gap-2 border-b bg-muted/40 px-4 py-1.5 text-xs" role="status">
                    <History className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    <span className="flex-1 text-muted-foreground">You're viewing older posts.</span>
                    <button type="button" onClick={() => setAnchor(null)} className="font-medium text-primary hover:underline">
                        Back to latest
                    </button>
                </div>
            ) : null}
            <div className="relative flex min-h-0 flex-1 flex-col" {...listDrop.dropProps}>
            {listDrop.isDragging ? <DropOverlay label={`Drop files to share in #${channelName}`} /> : null}
            <div
                ref={scrollerRef}
                onScroll={(e) => {
                    const el = e.currentTarget;
                    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
                }}
                className="min-h-0 flex-1 overflow-auto bg-muted/20 px-2 py-3 sm:px-4"
            >
                {posts.isPending ? (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading posts…
                    </p>
                ) : posts.isError ? (
                    <p className="text-sm text-destructive">Couldn't load this channel's posts. It will retry shortly.</p>
                ) : postList.length > 0 ? (
                    <>
                        {posts.hasNextPage ? (
                            <div className="mb-3 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => void posts.fetchNextPage()}
                                    disabled={posts.isFetchingNextPage}
                                    className="flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-xs font-medium shadow-sm hover:bg-accent disabled:opacity-60"
                                >
                                    {posts.isFetchingNextPage ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                                    Load older posts
                                </button>
                            </div>
                        ) : null}
                        <ul className="mx-auto flex max-w-4xl flex-col gap-3" aria-label={`Posts in #${channelName}`}>
                            {postList.map((p) => (
                                <PostCard
                                    key={p.message.id}
                                    channelId={channelId}
                                    channelName={channelName}
                                    post={p}
                                    members={members}
                                    canModerate={canModerate}
                                    readAt={readAt}
                                    receiptsByMessage={receiptsByMessage}
                                    onOpenThread={onOpenThread}
                                    onReply={(msg) => setReplyingTo(msg)}
                                />
                            ))}
                        </ul>
                        {firstPage?.hasNewer ? (
                            <div className="mt-3 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => setAnchor(null)}
                                    className="rounded-full border bg-card px-3 py-1 text-xs font-medium shadow-sm hover:bg-accent"
                                >
                                    Newer posts — back to latest
                                </button>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                        <Hash className="h-8 w-8" aria-hidden />
                        <p>No posts in #{channelName} yet. Start a post below.</p>
                    </div>
                )}
            </div>
            </div>
            {typingLabel ? (
                <div className="px-4 pb-1 text-xs italic text-muted-foreground">{typingLabel}</div>
            ) : null}
            {channelKind === "Announcement" && !canModerate ? (
                <p role="note" className="mx-4 mb-3 rounded-md border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                    Only the owners of this announcement channel can start posts. You can reply to a post from its thread.
                </p>
            ) : (
            <Composer
                channelId={channelId}
                channelName={channelName}
                members={members}
                attachments={attachments}
                draft={draft}
                onInput={onInput}
                replyingTo={replyingTo}
                onCancelReply={() => setReplyingTo(null)}
                canModerate={canModerate}
                onAfterSend={() => {
                    setDraft("");
                    clearDraft(channelId);
                    setReplyingTo(null);
                    if (isTypingRef.current) {
                        isTypingRef.current = false;
                        onTyping(false);
                    }
                }}
                sending={send.isPending}
                onSendHtml={(html, importance, post) => {
                    // A new post belongs at the bottom of the latest posts.
                    setAnchor(null);
                    return send.mutateAsync({
                        bodyHtml: html,
                        importance: importance && importance !== "Standard" ? importance : undefined,
                        subject: post?.subject,
                        isAnnouncement: post?.isAnnouncement
                    });
                }}
            />
            )}
                </>
            )}
        </>
    );
}

interface PostOptions {
    subject?: string | null;
    isAnnouncement?: boolean;
}

/**
 * "Start a post": the channel composer. Beyond the message itself a post can
 * carry a subject line, and team owners can make it an Announcement (the
 * subject becomes a headline on an accent banner). Polls come from the Poll
 * button and are posted as their own message.
 */
function Composer({
    channelId,
    channelName,
    members,
    attachments,
    draft,
    onInput,
    replyingTo,
    onCancelReply,
    canModerate,
    onAfterSend,
    sending,
    onSendHtml
}: {
    channelId: string;
    channelName: string;
    members: ChannelMember[];
    attachments: DriveAttachments;
    draft: string;
    onInput: (value: string) => void;
    replyingTo?: ChatMessage | null;
    onCancelReply?: () => void;
    /** Team owner / channel moderator: may post announcements. */
    canModerate: boolean;
    onAfterSend: () => void;
    sending: boolean;
    onSendHtml: (html: string, importance?: MessageImportance, post?: PostOptions) => Promise<ChatMessage>;
}) {
    const [subjectOpen, setSubjectOpen] = useState(false);
    const [subject, setSubject] = useState("");
    const [announcement, setAnnouncement] = useState(false);
    const [pollOpen, setPollOpen] = useState(false);
    useEffect(() => {
        setSubject("");
        setSubjectOpen(false);
        setAnnouncement(false);
    }, [channelId]);
    const headlineMissing = announcement && !subject.trim();
    const token = useAccessToken();
    const auth = useAuth();
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const composerDrop = useFileDropZone(attachments.addFiles);
    const [gifOpen, setGifOpen] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    // The draft as it is now — a send that waited on uploads must not wipe
    // what was typed in the meantime.
    const draftRef = useRef(draft);
    draftRef.current = draft;
    // The text area is the rich composer (features/rich); `draft` mirrors its HTML for the per-channel draft.
    const editor = useRef<RichEditorHandle>(null);
    const [empty, setEmpty] = useState(() => !draft);
    const mentionCandidates = useMemo(
        () => members.map((m) => ({ userId: m.userId, name: m.displayName || m.userEmail, email: m.userEmail })),
        [members]
    );
    // Switching channel loads that channel's saved draft; a sent post (draft reset to "") empties the box.
    useEffect(() => {
        editor.current?.setHtml(draftToHtml(loadDraft(channelId)), true);
        setEmpty(editor.current?.isEmpty() ?? true);
    }, [channelId]);
    useEffect(() => {
        if (draft === "" && editor.current && !editor.current.isEmpty()) editor.current.clear();
    }, [draft]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [importance, setImportance] = useState<MessageImportance>("Standard");
    const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
    const [meetingBusy, setMeetingBusy] = useState(false);
    const [meetingError, setMeetingError] = useState<string | null>(null);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessageDto[]>([]);

    const pendingFiles = attachments.files.filter((f) => !f.locked);

    const submitScheduled = async (sendAt: Date) => {
        const body = editor.current?.getHtml() ?? "";
        if (!body) return;
        try {
            const msg = await scheduledApi.createScheduled(channelId, body, sendAt, token);
            setScheduledMessages((prev) => [...prev, msg]);
            setScheduleOpen(false);
            onAfterSend();
        } catch {
            // Surfaced by the caller
        }
    };

    const submit = async () => {
        const typed = editor.current?.getHtml() ?? "";
        if (!typed && pendingFiles.length === 0) return;
        if (headlineMissing) {
            setSendError("Add a headline for the announcement.");
            return;
        }
        const snapshot = draft;
        setSendError(null);
        setPreparing(true);
        // Files go to the sender's Drive (uploaded when attached) and are
        // shared with the channel's members now; the message carries a marker
        // per file that renders as a card. Old messages keep their
        // BFF-stored attachments (AttachmentPreviews below).
        let keys: string[] = [];
        try {
            let markers = "";
            if (pendingFiles.length > 0) {
                const prepared = await attachments.prepareSend({
                    emails: members.map((m) => m.userEmail),
                    ownerEmail: myEmail,
                    source: "connect-channel",
                    sourceRef: channelId
                });
                markers = prepared.markersHtml;
                keys = prepared.keys;
            }
            // The rich composer's HTML (typed Markdown already applied).
            const body = typed;
            // Quoted-reply prefix — if the user is replying to a specific
            // message, lead with a "Replying to <author>" header + a
            // blockquote of the original. Strips the body to a sane length
            // so a 5 KB original doesn't bloat every reply on the channel.
            const quotedPrefix = replyingTo
                ? buildQuotedReplyHtml(replyingTo)
                : "";
            const html = quotedPrefix + body + markers;
            const cleanSubject = subject.trim() || null;
            const msg = await onSendHtml(html, importance, { subject: cleanSubject, isAnnouncement: announcement && Boolean(cleanSubject) });
            if (keys.length > 0) attachments.commit(keys);
            // Reset importance after a successful send — every post is
            // Standard by default; users opt in per message.
            setImportance("Standard");
            setSubject("");
            setSubjectOpen(false);
            setAnnouncement(false);
            // Keep anything typed while the files were still uploading.
            if (draftRef.current === snapshot) onAfterSend();
            else onCancelReply?.();
            if (typed.includes("@")) {
                void connectParityApi.parseMentions(channelId, msg.id, token).catch(() => {});
            }
        } catch (err) {
            if (keys.length > 0) attachments.release(keys);
            const msg = err instanceof Error ? err.message : "";
            setSendError(msg && !msg.startsWith("Connect BFF") ? msg : "Couldn't send — your message is still here. Try again.");
        } finally {
            setPreparing(false);
        }
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                void submit();
            }}
            {...composerDrop.dropProps}
            className="relative flex shrink-0 flex-col gap-2 border-t p-3"
        >
            {composerDrop.isDragging ? <DropOverlay label="Drop files to attach" /> : null}
            {replyingTo ? (
                <div className="flex items-start gap-2 rounded-md border-l-4 border-primary bg-primary/5 px-3 py-2 text-xs">
                    <CornerUpLeft className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                    <div className="min-w-0 flex-1">
                        <div className="font-medium">
                            Replying to {replyingTo.authorDisplayName ?? replyingTo.authorEmail}
                        </div>
                        <div
                            className="mt-0.5 line-clamp-2 break-words text-muted-foreground"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(replyingTo.bodyHtml) }}
                        />
                    </div>
                    {onCancelReply ? (
                        <button
                            type="button"
                            onClick={onCancelReply}
                            aria-label="Cancel reply"
                            title="Cancel reply"
                            className="rounded p-0.5 text-muted-foreground hover:bg-accent"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    ) : null}
                </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-xs font-semibold text-muted-foreground">Start a post</span>
                <button
                    type="button"
                    onClick={() => setSubjectOpen((v) => !v)}
                    aria-pressed={subjectOpen}
                    disabled={announcement}
                    className={cn(
                        "flex h-7 items-center gap-1 rounded-md border px-2 text-xs hover:bg-accent disabled:opacity-50",
                        subjectOpen && !announcement && "border-primary bg-primary/10 text-primary"
                    )}
                >
                    <Heading className="h-3.5 w-3.5" aria-hidden /> Subject
                </button>
                {canModerate ? (
                    <button
                        type="button"
                        onClick={() => setAnnouncement((v) => !v)}
                        aria-pressed={announcement}
                        title="Post as an announcement (team owners)"
                        className={cn(
                            "flex h-7 items-center gap-1 rounded-md border px-2 text-xs hover:bg-accent",
                            announcement && "border-primary bg-primary/10 text-primary"
                        )}
                    >
                        <Megaphone className="h-3.5 w-3.5" aria-hidden /> Announcement
                    </button>
                ) : null}
            </div>
            {announcement ? (
                <div className="rounded-md bg-gradient-to-r from-primary to-violet-600 px-3 py-2.5">
                    <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        maxLength={256}
                        placeholder="Type a headline"
                        aria-label="Announcement headline"
                        aria-invalid={headlineMissing}
                        className="w-full bg-transparent text-lg font-bold text-white outline-none placeholder:text-white/70"
                    />
                </div>
            ) : subjectOpen ? (
                <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    maxLength={256}
                    placeholder="Add a subject"
                    aria-label="Subject"
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm font-semibold outline-none focus:ring-2 focus:ring-primary"
                />
            ) : null}
            <ComposerAttachments attachments={attachments} />
            <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                    <RichInput
                        ref={editor}
                        initialHtml={draftToHtml(draft)}
                        placeholder={`Start a post in #${channelName}  \u2014  @ to mention \u00b7 paste or drop files to share`}
                        mentionCandidates={mentionCandidates}
                        everyone
                        onChange={(state) => { setEmpty(state.empty); if (state.html !== draftRef.current) onInput(state.html); }}
                        onSubmit={() => void submit()}
                        onPaste={(e) => {
                            const pasted = filesFromPaste(e);
                            if (pasted.length === 0) return false;
                            e.preventDefault();
                            attachments.addFiles(pasted);
                            return true;
                        }}
                    />
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) attachments.addFiles(e.target.files);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted disabled:opacity-50"
                    aria-label="Attach files"
                    title="Attach files"
                >
                    <Paperclip className="h-4 w-4" />
                </button>
                <EmojiPickerTrigger
                    onSelect={(emoji) => {
                        editor.current?.insertText(emoji);
                    }}
                />
                <button
                    type="button"
                    onClick={() => setGifOpen(true)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted"
                    aria-label="Pick a GIF"
                    title="Pick a GIF"
                >
                    <Sticker className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => setPollOpen(true)}
                    className="flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted"
                    aria-label="Create a poll"
                    title="Poll"
                >
                    <BarChart3 className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setMeetingError(null);
                        setMeetingDialogOpen(true);
                    }}
                    disabled={meetingBusy}
                    className="flex h-9 w-9 items-center justify-center rounded-md border hover:bg-muted disabled:opacity-50"
                    aria-label="Insert meeting link"
                    title="Insert meeting link"
                >
                    {meetingBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Video className="h-4 w-4" />
                    )}
                </button>
                <ImportancePicker value={importance} onChange={setImportance} />
                <div className="relative flex">
                    <button
                        type="submit"
                        disabled={sending || preparing || headlineMissing || (empty && pendingFiles.length === 0)}
                        className={cn(
                            "flex h-9 items-center gap-1 rounded-l-md px-3 text-sm text-primary-foreground disabled:opacity-50",
                            importance === "Urgent"
                                ? "bg-destructive hover:bg-destructive/90"
                                : importance === "Important"
                                    ? "bg-amber-600 hover:bg-amber-700"
                                    : "bg-primary hover:bg-primary/90"
                        )}
                    >
                        {preparing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {preparing && pendingFiles.some((f) => f.status === "uploading") ? "Uploading…" : "Send"}
                    </button>
                    <button
                        type="button"
                        onClick={() => setScheduleOpen((v) => !v)}
                        disabled={empty}
                        title="Schedule send"
                        aria-label="Schedule send"
                        className={cn(
                            "flex h-9 w-7 items-center justify-center rounded-r-md border-l text-primary-foreground disabled:opacity-50",
                            importance === "Urgent"
                                ? "border-l-destructive/50 bg-destructive hover:bg-destructive/90"
                                : importance === "Important"
                                    ? "border-l-amber-700/50 bg-amber-600 hover:bg-amber-700"
                                    : "border-l-primary/50 bg-primary hover:bg-primary/90"
                        )}
                    >
                        <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    {scheduleOpen ? (
                        <ScheduleSendDialog
                            onClose={() => setScheduleOpen(false)}
                            onSubmit={(sendAt) => void submitScheduled(sendAt)}
                        />
                    ) : null}
                </div>
            </div>
            {sendError ? <p role="alert" className="text-xs text-destructive">{sendError}</p> : null}
            {/* Scheduled queue — visible to sender only, below the composer row */}
            {scheduledMessages.length > 0 ? (
                <ScheduledQueue
                    channelId={channelId}
                    messages={scheduledMessages}
                    token={token}
                    onCancel={(id) =>
                        setScheduledMessages((prev) => prev.filter((m) => m.id !== id))
                    }
                />
            ) : null}
            <PollComposerDialog
                open={pollOpen}
                onOpenChange={setPollOpen}
                scope="channels"
                conversationId={channelId}
                onPost={async (html) => { await onSendHtml(html); }}
            />
            {gifOpen ? (
                <GifPicker
                    onClose={() => setGifOpen(false)}
                    onSelect={(url, alt) => {
                        // Send the GIF directly as its own message: the picker is a fire-and-forget short-circuit
                        // around the textarea. Wraps in a paragraph + img so
                        // the existing renderer pipes it through unchanged.
                        const safeAlt = alt.replace(/[<>"]/g, "");
                        const html = `<p><img src="${url}" alt="${safeAlt}" class="max-w-xs rounded-md" /></p>`;
                        void onSendHtml(html, importance);
                        setGifOpen(false);
                    }}
                />
            ) : null}
            {meetingDialogOpen ? (
                <ComposerMeetingDialog
                    busy={meetingBusy}
                    error={meetingError}
                    onClose={() => {
                        setMeetingDialogOpen(false);
                        setMeetingError(null);
                    }}
                    onSubmit={async ({ subject, startLocal, durationMinutes }) => {
                        setMeetingBusy(true);
                        setMeetingError(null);
                        try {
                            const start = new Date(startLocal);
                            const end = new Date(start.getTime() + durationMinutes * 60_000);
                            const finalSubject = subject || "Meeting";
                            const result = await connectApi.createMeeting(
                                {
                                    subject: finalSubject,
                                    startUtc: start.toISOString(),
                                    endUtc:   end.toISOString()
                                },
                                token
                            );
                            const html = renderMeetingCardHtml(result, start, end);
                            await onSendHtml(html, importance);
                            setMeetingDialogOpen(false);
                            onAfterSend();
                        } catch (err) {
                            setMeetingError(err instanceof Error ? err.message : String(err));
                        } finally {
                            setMeetingBusy(false);
                        }
                    }}
                />
            ) : null}
        </form>
    );
}

function renderMeetingCardHtml(
    meeting: { provider: string; joinUrl: string; subject: string },
    start: Date,
    end: Date
): string {
    const fmt = (d: Date) =>
        d.toLocaleString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    const safe = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const providerLabel =
        meeting.provider === "teams"
            ? "Microsoft Teams"
            : meeting.provider.charAt(0).toUpperCase() + meeting.provider.slice(1);
    return (
        `<div class="rounded-md border bg-muted/40 p-3" style="font-family:Arial,Helvetica,sans-serif">` +
        `<div style="font-size:12px;color:#5f5f5f;text-transform:uppercase;letter-spacing:0.04em">${safe(providerLabel)} meeting</div>` +
        `<div style="font-weight:600;font-size:14px;margin-top:2px">${safe(meeting.subject)}</div>` +
        `<div style="font-size:12px;color:#5f5f5f;margin-top:2px">${safe(fmt(start))} – ${safe(fmt(end))}</div>` +
        `<a href="${meeting.joinUrl}" style="display:inline-block;margin-top:8px;color:#0078d4;text-decoration:underline;font-size:13px">Click here to join the meeting</a>` +
        `</div>`
    );
}

interface ComposerMeetingDialogForm {
    subject: string;
    startLocal: string;
    durationMinutes: number;
}

function ComposerMeetingDialog({
    busy,
    error,
    onClose,
    onSubmit
}: {
    busy: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (form: ComposerMeetingDialogForm) => Promise<void> | void;
}) {
    const initial = useMemo(() => {
        const d = new Date();
        d.setMinutes(d.getMinutes() + 5 - (d.getMinutes() % 5));
        d.setSeconds(0);
        d.setMilliseconds(0);
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }, []);
    const [subject, setSubject] = useState("");
    const [startLocal, setStartLocal] = useState(initial);
    const [durationMinutes, setDurationMinutes] = useState(30);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Insert meeting link"
            onClick={onClose}
        >
            <div className="w-full max-w-md rounded-md border bg-background p-5 shadow-lg" onClick={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-base font-semibold">Insert meeting link</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Cancel"
                        className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-muted"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="space-y-3 text-sm">
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-muted-foreground">Subject</span>
                        <input
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Project sync"
                            autoFocus
                            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-muted-foreground">Start</span>
                        <input
                            type="datetime-local"
                            value={startLocal}
                            onChange={(e) => setStartLocal(e.target.value)}
                            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-muted-foreground">Duration</span>
                        <select
                            value={durationMinutes}
                            onChange={(e) => setDurationMinutes(Number(e.target.value))}
                            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                        >
                            <option value={15}>15 minutes</option>
                            <option value={30}>30 minutes</option>
                            <option value={45}>45 minutes</option>
                            <option value={60}>1 hour</option>
                            <option value={90}>1.5 hours</option>
                            <option value={120}>2 hours</option>
                        </select>
                    </label>
                    {error ? (
                        <p className="rounded border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                            {error}
                        </p>
                    ) : null}
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={busy}
                        className="h-9 rounded-md px-3 text-sm hover:bg-muted disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void onSubmit({ subject, startLocal, durationMinutes })}
                        disabled={busy || !startLocal}
                        className="flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Insert &amp; Send
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Three-state importance picker — Standard / Important / Urgent.
 * The "!" priority menu next to the send button. Cycles
 * forward on each click; right-click resets to Standard so users can
 * undo a misclick without re-cycling.
 */
function ImportancePicker({
    value,
    onChange
}: {
    value: MessageImportance;
    onChange: (v: MessageImportance) => void;
}) {
    const next: MessageImportance =
        value === "Standard" ? "Important" : value === "Important" ? "Urgent" : "Standard";
    return (
        <button
            type="button"
            onClick={() => onChange(next)}
            onContextMenu={(e) => { e.preventDefault(); onChange("Standard"); }}
            className={cn(
                "flex h-9 w-9 items-center justify-center rounded-md border text-xs",
                value === "Urgent"
                    ? "border-destructive bg-destructive/10 text-destructive"
                    : value === "Important"
                        ? "border-amber-500 bg-amber-500/10 text-amber-600"
                        : "border-border text-muted-foreground hover:bg-accent"
            )}
            aria-label={`Importance: ${value}. Click to change.`}
            title={`${value} — click to cycle, right-click to reset`}
        >
            {value === "Urgent"
                ? <Siren className="h-4 w-4" />
                : value === "Important"
                    ? <AlertTriangle className="h-4 w-4" />
                    : <Flag className="h-4 w-4" />}
        </button>
    );
}

/**
 * One channel post as a card: an announcement banner
 * when it's one, the post itself with its actions, then its thread — reply
 * count, the latest repliers and a "New" mark when others replied since you
 * last read the channel — and a collapsed "Reply" composer.
 */
function PostCard({
    channelId,
    channelName,
    post,
    members,
    canModerate,
    readAt,
    receiptsByMessage,
    onOpenThread,
    onReply
}: {
    channelId: string;
    channelName: string;
    post: ChannelPost;
    members: ChannelMember[];
    canModerate: boolean;
    /** When I last read the channel before this visit; null = unknown. */
    readAt: string | null;
    receiptsByMessage?: Map<string, ChannelMember[]>;
    onOpenThread: (msg: ChatMessage, highlightReplyId?: string | null) => void;
    onReply?: (msg: ChatMessage) => void;
}) {
    const auth = useAuth();
    const me = normalizeUserId(auth.user?.profile.sub);
    const [replying, setReplying] = useState(false);
    const m = post.message;
    const readTime = readAt ? new Date(readAt).getTime() : null;
    const isNew = readTime !== null && normalizeUserId(m.authorUserId) !== me && new Date(m.sentAt).getTime() > readTime;
    const newReplies = readTime !== null && Boolean(post.lastReplyFromOthersAt)
        && new Date(post.lastReplyFromOthersAt!).getTime() > readTime;

    return (
        <li
            id={`msg-${m.id}`}
            className={cn(
                "scroll-mt-16 overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm",
                m.isAnnouncement && "border-primary/40"
            )}
        >
            {m.isAnnouncement && m.subject ? (
                <div className="bg-gradient-to-r from-primary via-primary to-violet-600 px-4 py-4 text-white">
                    <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider opacity-90">
                        <Megaphone className="h-3.5 w-3.5" aria-hidden /> Announcement
                    </div>
                    <h2 className="mt-1 break-words text-lg font-bold leading-tight sm:text-xl">{m.subject}</h2>
                </div>
            ) : null}
            <MessageItem
                channelId={channelId}
                message={m}
                canModerate={canModerate}
                isNew={isNew}
                receiptsByMessage={receiptsByMessage}
                onOpenThread={onOpenThread}
                onReply={onReply}
            />
            <div className="border-t bg-muted/20">
                {post.replyCount > 0 ? (
                    <button
                        type="button"
                        onClick={() => onOpenThread(m)}
                        aria-label={`${post.replyCount} ${post.replyCount === 1 ? "reply" : "replies"}${newReplies ? ", including new replies" : ""}. Open the thread`}
                        className="flex w-full flex-wrap items-center gap-2 px-4 py-2 text-left text-xs hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    >
                        <span className="flex -space-x-1.5" aria-hidden>
                            {post.repliers.map((r) => (
                                <span
                                    key={r.userId}
                                    title={r.name}
                                    className={cn("flex h-6 w-6 items-center justify-center rounded-full border-2 border-card text-[9px] font-semibold text-white", colourFor(r.name))}
                                >
                                    {initials(r.name)}
                                </span>
                            ))}
                        </span>
                        <span className="font-semibold text-primary">
                            {post.replyCount} {post.replyCount === 1 ? "reply" : "replies"}
                        </span>
                        {newReplies ? (
                            <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">New</span>
                        ) : null}
                        {post.lastReplyAt ? (
                            <span className="text-muted-foreground">Last reply {listTime(post.lastReplyAt)}</span>
                        ) : null}
                    </button>
                ) : null}
                {replying ? (
                    <InlineReplyComposer
                        channelId={channelId}
                        channelName={channelName}
                        postId={m.id}
                        members={members}
                        onDone={() => setReplying(false)}
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => setReplying(true)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-medium text-muted-foreground hover:bg-accent/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    >
                        <CornerDownRight className="h-3.5 w-3.5" aria-hidden /> Reply
                    </button>
                )}
            </div>
        </li>
    );
}

/** The expanded "Reply" box under a post: the chat composer (Markdown, emoji, @mentions, Drive files). */
function InlineReplyComposer({ channelId, channelName, postId, members, onDone }: {
    channelId: string;
    channelName: string;
    postId: string;
    members: ChannelMember[];
    onDone: () => void;
}) {
    const auth = useAuth();
    const me = normalizeUserId(auth.user?.profile.sub);
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const send = useSendThreadReply(channelId, postId);
    const attachments = useDriveAttachments("view");
    const mentionCandidates = useMemo<MentionCandidate[]>(() =>
        members
            .filter((m) => normalizeUserId(m.userId) !== me)
            .map((m) => ({ userId: m.userId, name: m.displayName || m.userEmail, email: m.userEmail })),
    [members, me]);
    const resolveShare = async (): Promise<ChatShareTarget> => ({
        emails: members.map((m) => m.userEmail).filter((e) => e && e.toLowerCase() !== myEmail),
        ownerEmail: myEmail,
        source: "connect-channel",
        sourceRef: channelId
    });
    return (
        <div
            className="border-t"
            onKeyDown={(e) => {
                if (e.key === "Escape" && !e.defaultPrevented) onDone();
            }}
        >
            <div className="flex items-center justify-between px-4 pt-1.5 text-[11px] text-muted-foreground">
                <span>Replying in #{channelName}</span>
                <button
                    type="button"
                    onClick={onDone}
                    aria-label="Close reply"
                    className="rounded p-0.5 hover:bg-accent hover:text-foreground"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
            <ChatComposer
                placeholder="Reply"
                onSend={async (html) => { await send.mutateAsync(html); }}
                attachments={attachments}
                resolveShare={resolveShare}
                mentionCandidates={mentionCandidates}
                requireRecipients={false}
            />
        </div>
    );
}

function MessageItem({
    channelId,
    message,
    canModerate,
    isNew,
    receiptsByMessage,
    onOpenThread,
    onReply
}: {
    channelId: string;
    message: ChatMessage;
    /** Team owner / channel moderator: may pin. */
    canModerate?: boolean;
    /** Posted by someone else since I last read the channel. */
    isNew?: boolean;
    receiptsByMessage?: Map<string, ChannelMember[]>;
    onOpenThread: (msg: ChatMessage, highlightReplyId?: string | null) => void;
    onReply?: (msg: ChatMessage) => void;
}) {
    const action = useMessageAction(channelId);
    const pinToggle = usePinToggle("channels", channelId);
    const auth = useAuth();
    const [showReactions, setShowReactions] = useState(false);
    const [forwarding, setForwarding] = useState(false);
    const [translation, setTranslation] = useState<{ text: string | null; provider: string } | null>(null);
    const [translating, setTranslating] = useState(false);
    const [editing, setEditing] = useState(false);
    // The edit box is the rich composer, loaded with the post's HTML; its files stay out of it and are put back on save.
    const [editHtml, setEditHtml] = useState("");
    const editor = useRef<RichEditorHandle>(null);

    // Only the author may edit — the server enforces this too (403 otherwise).
    const isOwn = !!message.authorUserId
        && normalizeUserId(message.authorUserId) === normalizeUserId(auth.user?.profile.sub);

    const beginEdit = () => {
        // The post's own HTML goes into the editor, so its formatting is kept and editable.
        // Files are carried over untouched — only the text is edited.
        setEditHtml(splitFileMarkers(message.bodyHtml, { legacy: false }).html);
        setEditing(true);
    };
    const saveEdit = () => {
        const html = editor.current?.getHtml() ?? "";
        const files = fileMarkersOf(message.bodyHtml);
        if (!html && !files) { setEditing(false); return; }
        action.mutate({ type: "edit", messageId: message.id, bodyHtml: html + files });
        setEditing(false);
    };

    const importance: MessageImportance = (message.importance ?? "Standard") as MessageImportance;
    // A poll post isn't edited — the poll card is the content.
    const editable = isOwn && !hasPollMarker(message.bodyHtml);
    return (
        <div
            className={cn(
                "group relative flex gap-3 px-3 py-2.5",
                importance === "Urgent"
                    ? "border-l-4 border-destructive bg-destructive/5"
                    : importance === "Important"
                        ? "border-l-4 border-amber-500 bg-amber-500/5"
                        : null
            )}
            onMouseLeave={() => setShowReactions(false)}
        >
            <div className="h-8 w-8 shrink-0 rounded-full bg-muted text-center text-xs leading-8 font-semibold">
                {initials(message.authorDisplayName ?? message.authorEmail)}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium">
                        {message.authorDisplayName ?? message.authorEmail}
                    </span>
                    <span className="text-xs text-muted-foreground">
                        {new Date(message.sentAt).toLocaleString()}
                    </span>
                    {message.isEdited ? (
                        <span className="text-[10px] text-muted-foreground">(edited)</span>
                    ) : null}
                    {message.isPinned ? (
                        <Pin className="h-3 w-3 text-amber-500" aria-label="Pinned" />
                    ) : null}
                    {isNew ? (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">New</span>
                    ) : null}
                    {importance === "Urgent" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-destructive px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-destructive-foreground">
                            <Siren className="h-3 w-3" /> Urgent
                        </span>
                    ) : importance === "Important" ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                            <AlertTriangle className="h-3 w-3" /> Important
                        </span>
                    ) : null}
                </div>
                {editing ? (
                    <div className="mt-0.5">
                        <RichInput
                            ref={editor}
                            initialHtml={editHtml}
                            placeholder="Edit message"
                            ariaLabel="Edit message"
                            autoFocus
                            keepImages
                            enterAlwaysSubmits
                            onSubmit={saveEdit}
                            onEscape={() => setEditing(false)}
                        />
                        <div className="mt-1 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={saveEdit}
                                disabled={action.isPending}
                                className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            >
                                <Check className="h-3.5 w-3.5" /> Save
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditing(false)}
                                className="rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                            >
                                Cancel
                            </button>
                            <span className="text-[10px] text-muted-foreground">Enter to save · Esc to cancel</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {message.subject && !message.isAnnouncement ? (
                            <h3 className="mt-0.5 break-words text-base font-semibold">{message.subject}</h3>
                        ) : null}
                        <MessageBody html={message.bodyHtml} author={message.authorDisplayName ?? message.authorEmail} className="prose prose-sm max-w-none break-words dark:prose-invert" />
                        <UnfurlPreviews bodyHtml={message.bodyHtml} />
                    </>
                )}
                {translation ? (
                    <div className="mt-1 flex items-start gap-2 rounded-md border-l-4 border-primary bg-primary/5 px-3 py-1.5 text-xs">
                        <Languages className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                Translated · {translation.provider}
                            </div>
                            <div className="mt-0.5 break-words">
                                {translation.text ?? <em className="text-muted-foreground">Translation unavailable.</em>}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setTranslation(null)}
                            aria-label="Dismiss translation"
                            className="rounded p-0.5 text-muted-foreground hover:bg-accent"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                ) : null}
                <AttachmentPreviews channelId={channelId} messageId={message.id} />
                <ReactionChips channelId={channelId} messageId={message.id} />
                {receiptsByMessage ? (
                    <ReadReceiptAvatars
                        message={message}
                        receiptsByMessage={receiptsByMessage}
                    />
                ) : null}
            </div>
            <div className="absolute right-2 top-1 hidden items-center gap-1 rounded-md border bg-card p-1 shadow-sm group-focus-within:flex group-hover:flex">
                {showReactions ? (
                    <div className="flex items-center gap-0.5 pr-1">
                        {QUICK_REACTIONS.map((emoji) => (
                            <button
                                key={emoji}
                                type="button"
                                onClick={() => {
                                    action.mutate({ type: "react", messageId: message.id, emoji });
                                    setShowReactions(false);
                                }}
                                className="rounded px-1 text-sm hover:bg-muted"
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setShowReactions(true)}
                        className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                        aria-label="Add reaction"
                    >
                        <Smile className="h-3.5 w-3.5" />
                    </button>
                )}
                {onReply ? (
                    <button
                        type="button"
                        onClick={() => onReply(message)}
                        className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                        aria-label="Reply"
                        title="Reply with quote"
                    >
                        <CornerUpLeft className="h-3.5 w-3.5" />
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={() => onOpenThread(message)}
                    className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                    aria-label="Reply in thread"
                    title="Reply in thread"
                >
                    <MessageSquareText className="h-3.5 w-3.5" />
                </button>
                {canModerate ? (
                    <button
                        type="button"
                        onClick={() => pinToggle.mutate({ messageId: message.id, pin: !message.isPinned })}
                        disabled={pinToggle.isPending}
                        className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted disabled:opacity-50"
                        aria-label={message.isPinned ? "Unpin" : "Pin"}
                        title={message.isPinned ? "Unpin" : "Pin"}
                    >
                        {message.isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                    </button>
                ) : null}
                <SaveMessageButton channelId={channelId} messageId={message.id} />
                <TranslateButton
                    message={message}
                    busy={translating}
                    onResult={(t) => setTranslation(t)}
                    onBusy={setTranslating}
                />
                <ConvertToTaskButton message={message} channelId={channelId} />
                <button
                    type="button"
                    onClick={() => setForwarding(true)}
                    aria-label="Forward"
                    title="Forward"
                    className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                >
                    <Forward className="h-3.5 w-3.5" />
                </button>
                {editable ? (
                    <button
                        type="button"
                        onClick={beginEdit}
                        className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                        aria-label="Edit message"
                        title="Edit"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={() => {
                        void confirmDialog({ title: "Delete this message?", confirmLabel: "Delete", danger: true }).then((ok) => {
                            if (ok) action.mutate({ type: "delete", messageId: message.id });
                        });
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded text-destructive hover:bg-muted"
                    aria-label="Delete"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
            {forwarding ? (
                <ForwardMessageDialog message={message} onClose={() => setForwarding(false)} />
            ) : null}
        </div>
    );
}

function SaveMessageButton({ channelId, messageId }: { channelId: string; messageId: string }) {
    const save = useSaveChannelMessage();
    return (
        <button
            type="button"
            onClick={() => save.mutate({ channelId, messageId })}
            disabled={save.isPending}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted disabled:opacity-50"
            aria-label="Save message"
            title="Save for later"
        >
            <Bookmark className="h-3.5 w-3.5" />
        </button>
    );
}

/**
 * Translate-this-message — fetches /api/translate with the user's
 * current locale (or English when locale resolution fails) and bubbles
 * the result up to the parent so a translation pill renders inline.
 */
function TranslateButton({
    message,
    busy,
    onResult,
    onBusy
}: {
    message: ChatMessage;
    busy: boolean;
    onResult: (t: { text: string | null; provider: string }) => void;
    onBusy: (b: boolean) => void;
}) {
    const token = useAccessToken();
    const t = useT();
    const onClick = async () => {
        const target = navigator.language ?? "en";
        onBusy(true);
        try {
            const res = await connectParityApi.translate(message.bodyHtml, target, token);
            onResult({ text: res.translated, provider: res.provider });
        } catch {
            onResult({ text: null, provider: "error" });
        } finally {
            onBusy(false);
        }
    };
    return (
        <button
            type="button"
            onClick={() => void onClick()}
            disabled={busy}
            aria-label={t("msg.translate", "Translate")}
            title={t("msg.translate", "Translate")}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted disabled:opacity-50"
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
        </button>
    );
}

/**
 * "Convert to task" — POSTs to the Mail BFF Tasks API with title set
 * to the message body excerpt + a back-link in notes pointing at the
 * source channel/message. On success, prompts the user to jump to
 * the Mail tasks page in a new tab.
 */
function ConvertToTaskButton({
    message,
    channelId
}: {
    message: ChatMessage;
    channelId: string;
}) {
    const token = useAccessToken();
    const [busy, setBusy] = useState(false);

    const onClick = async () => {
        const text = message.bodyHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
        if (!text) return;
        const title = text.length > 200 ? `${text.slice(0, 200)}…` : text;
        const author = message.authorDisplayName ?? message.authorEmail;
        const notes = `From ${author} on ${new Date(message.sentAt).toLocaleString()}\n` +
            `Channel: ${channelId}\n` +
            `Message: ${message.id}\n\n` +
            text;
        setBusy(true);
        try {
            await mailTasksApi.createFromChat(
                { title, notes, relatedMessageId: message.id },
                token
            );
            const open = await confirmDialog({ title: "Task created from this message.", message: "Open the Mail Tasks page now?", confirmLabel: "Open", cancelLabel: "Not now" });
            if (open) window.open(mailTasksApi.tasksPageUrl(), "_blank", "noopener,noreferrer");
        } catch (e) {
            void alertDialog({ title: "Couldn't convert to task", message: e instanceof Error ? e.message : "unknown error" });
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={() => void onClick()}
            disabled={busy}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted disabled:opacity-50"
            aria-label="Convert to task"
            title="Convert to task"
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckSquare className="h-3.5 w-3.5" />}
        </button>
    );
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

// ─────────────────────────────────────────────────────────────────────────────
// Schedule Send UI
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Small popover that lets the user choose a future datetime and then calls
 * onSubmit with a Date object. Defaults to the next business hour.
 */
function ScheduleSendDialog({
    onClose,
    onSubmit,
}: {
    onClose: () => void;
    onSubmit: (sendAt: Date) => void;
}) {
    const initial = useMemo(() => {
        const d = new Date();
        // Round up to next hour; if after 17:00 push to 09:00 next morning
        d.setMinutes(0, 0, 0);
        d.setHours(d.getHours() + 1);
        if (d.getHours() >= 18) {
            d.setDate(d.getDate() + 1);
            d.setHours(9, 0, 0, 0);
            // Skip to Monday if weekend
            if (d.getDay() === 0) d.setDate(d.getDate() + 1);
            if (d.getDay() === 6) d.setDate(d.getDate() + 2);
        }
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }, []);

    const [sendAt, setSendAt] = useState(initial);

    // Close on outside click / Escape
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div
            className="absolute bottom-full right-0 z-50 mb-2 w-72 rounded-md border bg-card p-4 shadow-xl"
            role="dialog"
            aria-label="Schedule send"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-sm font-medium">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    Schedule send
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                    aria-label="Close"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
                Send at
            </label>
            <input
                type="datetime-local"
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                autoFocus
            />
            <div className="mt-3 flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onClose}
                    className="h-8 rounded-md px-3 text-xs hover:bg-muted"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={!sendAt}
                    onClick={() => {
                        if (!sendAt) return;
                        onSubmit(new Date(sendAt));
                    }}
                    className="flex h-8 items-center gap-1 rounded-md bg-primary px-3 text-xs text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                    <Clock className="h-3.5 w-3.5" />
                    Schedule
                </button>
            </div>
        </div>
    );
}

/**
 * Shows the sender's pending scheduled messages for this channel.
 * Displayed inline below the composer — only the sender can see it.
 */
function ScheduledQueue({
    channelId,
    messages,
    token,
    onCancel,
}: {
    channelId: string;
    messages: ScheduledMessageDto[];
    token: string | undefined;
    onCancel: (id: string) => void;
}) {
    const [cancelling, setCancelling] = useState<string | null>(null);
    if (messages.length === 0) return null;

    const doCancel = async (id: string) => {
        setCancelling(id);
        try {
            await scheduledApi.cancelScheduled(channelId, id, token);
            onCancel(id);
        } catch {
            // Silent — the row may have already been sent
        } finally {
            setCancelling(null);
        }
    };

    return (
        <div className="mt-2 flex flex-col gap-1 rounded-md border border-dashed border-border bg-muted/30 p-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Scheduled messages
            </p>
            {messages.map((m) => (
                <div key={m.id} className="flex items-center gap-2 text-xs">
                    <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate text-muted-foreground">
                        {new Date(m.sendAt).toLocaleString(undefined, {
                            month: "short", day: "numeric",
                            hour: "numeric", minute: "2-digit"
                        })}
                    </span>
                    <button
                        type="button"
                        disabled={cancelling === m.id}
                        onClick={() => void doCancel(m.id)}
                        className="rounded px-1.5 py-0.5 text-[10px] text-destructive hover:bg-destructive/10 disabled:opacity-50"
                    >
                        {cancelling === m.id ? "…" : "Cancel"}
                    </button>
                </div>
            ))}
        </div>
    );
}

/**
 * Build the inline blockquote that prefixes a quoted reply. Strips
 * existing tags from the original body and truncates to 240 chars so
 * deeply-nested reply chains stay readable.
 */
function buildQuotedReplyHtml(original: ChatMessage): string {
    const author = escapeHtml(original.authorDisplayName ?? original.authorEmail);
    const text = original.bodyHtml.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const truncated = text.length > 240 ? `${text.slice(0, 240)}…` : text;
    return [
        `<blockquote class="border-l-4 border-primary/60 pl-2 text-xs text-muted-foreground">`,
        `<strong>${author}</strong>: ${escapeHtml(truncated)}`,
        `</blockquote>`
    ].join("");
}

function ReactionChips({ channelId, messageId }: { channelId: string; messageId: string }) {
    const auth = useAuth();
    const reactions = useMessageReactions(channelId, messageId);
    const action = useMessageAction(channelId);
    const myId = auth.user?.profile.sub;

    if (!reactions.data || reactions.data.length === 0) return null;

    return (
        <div className="mt-1 flex flex-wrap gap-1">
            {reactions.data.map((r) => {
                const mine = myId !== undefined && r.userIds.includes(myId);
                return (
                    <button
                        key={r.emoji}
                        type="button"
                        onClick={() =>
                            action.mutate({
                                type: mine ? "unreact" : "react",
                                messageId,
                                emoji: r.emoji
                            })
                        }
                        title={`${r.count} ${r.count === 1 ? "person" : "people"} reacted with ${r.emoji}`}
                        aria-pressed={mine}
                        className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
                            mine
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-input bg-muted/40 hover:bg-muted"
                        )}
                    >
                        <span>{r.emoji}</span>
                        <span className="font-medium">{r.count}</span>
                    </button>
                );
            })}
        </div>
    );
}
